import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Trophy, MapPin, House, Path, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { frDate, frTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";

export default function Competitions() {
    const { data: champs = [], isLoading } = useQuery({ queryKey: ["championships"], queryFn: async () => (await api.get("/championships")).data });
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
    const now = new Date();
    const upcoming = champs.filter((c) => new Date(c.date) >= now);
    const past = champs.filter((c) => new Date(c.date) < now).reverse();

    const resultStyle = (r) => {
        if (r === "Victoire") return "bg-emerald-100 text-emerald-700";
        if (r === "Défaite") return "bg-red-100 text-red-700";
        if (r === "Match nul") return "bg-slate-200 text-slate-700";
        return "";
    };

    const Row = ({ c, i }) => {
        const home = c.home_away === "Domicile";
        const hasResult = c.result && c.result !== "À venir";
        return (
            <motion.div
                data-testid="championship-card"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="brand-card p-5 md:p-6 flex items-center gap-5"
            >
                <div className="shrink-0 flex flex-col items-center justify-center rounded-lg bg-navy text-white w-16 py-2">
                    <span className="font-mono text-[10px] uppercase text-slate-300">{frDate(c.date, { day: undefined, year: undefined, month: "short" })}</span>
                    <span className="font-display font-black text-2xl leading-none">{new Date(c.date).getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="rounded-full bg-clay/10 text-clay text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">{c.category}</span>
                        {c.division && <span className="text-xs text-slate-400">{c.division}</span>}
                    </div>
                    <div className="font-display font-bold text-navy leading-snug">{c.team_name}</div>
                    <div className="text-sm text-slate-500">vs {c.opponent || "à définir"}</div>
                    {(hasResult || c.score) && (
                        <div className="mt-1.5 flex items-center gap-2" data-testid="championship-result">
                            {hasResult && <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${resultStyle(c.result)}`}>{c.result}</span>}
                            {c.score && <span className="font-mono text-sm font-bold text-navy">{c.score}</span>}
                        </div>
                    )}
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">{frDate(c.date, { weekday: "short" })} · {frTime(c.date)}</span>
                        <span className="inline-flex items-center gap-1"><MapPin size={14} /> {c.location}</span>
                    </div>
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${home ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>
                    {home ? <House size={14} weight="fill" /> : <Path size={14} weight="fill" />}
                    {c.home_away}
                </span>
            </motion.div>
        );
    };

    return (
        <div data-testid="competitions-page">
            <PageHeader
                testid="competitions-header"
                overline="Par équipes"
                title="Compétitions & championnats"
                subtitle="Le calendrier des rencontres de nos équipes séniors et jeunes. Venez encourager les couleurs du club !"
            />
            <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 space-y-12">
                {settings?.tenup_championship_url && (
                    <a
                        href={settings.tenup_championship_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="championship-tenup-link"
                        className="flex items-center justify-between gap-4 brand-card bg-navy text-white p-5 md:p-6 relative overflow-hidden group"
                    >
                        <div className="absolute inset-0 court-lines opacity-20" />
                        <div className="relative">
                            <div className="overline text-clay mb-1">Ten'Up · Championnat</div>
                            <div className="font-display font-bold text-lg">Suivre le championnat sur Ten'Up</div>
                            <p className="text-slate-300 text-sm mt-1">Classements, résultats et calendrier officiels.</p>
                        </div>
                        <span className="relative shrink-0 inline-flex items-center gap-2 rounded-full bg-clay px-5 py-3 text-sm font-bold transition-transform duration-200 group-hover:-translate-y-0.5">
                            Ouvrir <ArrowSquareOut weight="bold" size={16} />
                        </span>
                    </a>
                )}
                <section>
                    <div className="flex items-center gap-3 mb-5">
                        <Trophy weight="duotone" size={26} className="text-clay" />
                        <h2 className="font-display font-extrabold text-2xl text-navy tracking-tight">Prochaines rencontres</h2>
                    </div>
                    {isLoading ? (
                        <p className="text-slate-500 font-mono text-sm">Chargement…</p>
                    ) : upcoming.length === 0 ? (
                        <p className="text-slate-500" data-testid="competitions-empty">Aucune rencontre programmée pour le moment.</p>
                    ) : (
                        <div className="grid gap-3 lg:grid-cols-2">
                            {upcoming.map((c, i) => <Row key={c.id} c={c} i={i} />)}
                        </div>
                    )}
                </section>

                {past.length > 0 && (
                    <section>
                        <h2 className="font-display font-extrabold text-2xl text-navy mb-5 tracking-tight">Rencontres passées</h2>
                        <div className="grid gap-3 lg:grid-cols-2 opacity-70">
                            {past.map((c, i) => <Row key={c.id} c={c} i={i} />)}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
